/**
 * Cart Kit Validation Module
 *
 * Validates that OpenCOTS heatframe products in the cart have
 * matching wedgelocks and ejectors as configured in cart attributes.
 *
 * Prevents checkout if incomplete kits are detected.
 */

class CartKitValidator {
  constructor() {
    this.validationResultCache = null;
  }

  /**
   * Validates all heatframe kits in the cart
   * @param {Object} cart - The Shopify cart object with items and attributes
   * @returns {Object} Validation result with valid flag, message, and missing components
   */
  validateHeatframeKits(cart) {
    // Debug logging
    console.log('[Cart Kit Validation] Starting validation');
    console.log('[Cart Kit Validation] Cart attributes:', cart?.attributes);
    console.log('[Cart Kit Validation] Cart items:', cart?.items?.map(item => ({
      title: item.product_title,
      sku: item.sku,
      quantity: item.quantity
    })));

    // No items in cart = valid
    if (!cart || !cart.items || cart.items.length === 0) {
      console.log('[Cart Kit Validation] Empty cart - validation passed');
      return { valid: true };
    }

    // Get configured component SKUs from cart attributes
    const configuredHeatframeSku = cart.attributes?.opencots_heatframe_sku;
    const configuredWedgelockSku = cart.attributes?.opencots_wedgelock_sku;
    const configuredEjectorLeftSku = cart.attributes?.opencots_ejector_sku_left;
    const configuredEjectorRightSku = cart.attributes?.opencots_ejector_sku_right;

    console.log('[Cart Kit Validation] Configured SKUs:', {
      heatframe: configuredHeatframeSku,
      wedgelock: configuredWedgelockSku,
      ejectorLeft: configuredEjectorLeftSku,
      ejectorRight: configuredEjectorRightSku
    });

    // Find heatframe line items that match the configured SKU
    const heatframes = cart.items.filter(item =>
      item.sku === configuredHeatframeSku && this.isValidSku(configuredHeatframeSku)
    );

    console.log('[Cart Kit Validation] Found heatframes:', heatframes.length);

    // No configured heatframes in cart = valid (allows standalone components)
    if (heatframes.length === 0) {
      console.log('[Cart Kit Validation] No heatframes in cart - validation passed');
      return { valid: true };
    }

    // Check if cart attributes exist for configuration
    if (!this.isValidSku(configuredHeatframeSku) ||
        !this.isValidSku(configuredWedgelockSku) ||
        !this.isValidSku(configuredEjectorLeftSku)) {
      console.log('[Cart Kit Validation] Invalid configuration attributes - validation failed');
      return {
        valid: false,
        message: 'Heatframe only sold with matching wedgelocks and ejectors',
        missingComponents: ['configuration'],
        configUrl: this.getConfiguratorUrl()
      };
    }

    // Task 200: Calculate total required quantities based on all heatframes
    // Use the heatframe's actual quantity, respecting variant-level minimum
    const totalHeatframeQty = heatframes.reduce((sum, item) => sum + item.quantity, 0);

    // Task 200: Find the heatframe variant to get its minimum order quantity
    // This ensures we validate against the correct minimum (variant → product → site)
    let heatframeMinQty = 1; // Default fallback
    if (heatframes.length > 0) {
      const heatframeItem = heatframes[0];
      // The minimum is enforced on the quantity input, so we can trust the quantity in cart
      // But we should validate it meets the multiplier requirements for components
      heatframeMinQty = Math.max(1, heatframeItem.quantity);
    }

    const requiredWedgelockQty = totalHeatframeQty * 2; // Always 2 wedgelocks per heatframe

    // Check if ejector is universal (single ejector works on both sides) or paired (left/right different)
    // 'PLACEHOLDER' = Universal ejector (no right ejector needed, but need 2x for left SKU)
    // Actual SKU or 'PAIRED_PLACEHOLDER' = Paired ejectors (need 1x left + 1x right)
    const isUniversal = configuredEjectorRightSku === 'PLACEHOLDER';
    const requiredEjectorLeftQty = isUniversal ? totalHeatframeQty * 2 : totalHeatframeQty; // Universal: 2 per board, Paired: 1 per board
    const requiredEjectorRightQty = isUniversal ? 0 : totalHeatframeQty; // Universal: 0, Paired: 1 per board

    // Validate wedgelocks
    const wedgelocks = cart.items.filter(item => item.sku === configuredWedgelockSku);
    const actualWedgelockQty = wedgelocks.reduce((sum, item) => sum + item.quantity, 0);

    console.log('[Cart Kit Validation] Wedgelock validation:', {
      required: requiredWedgelockQty,
      actual: actualWedgelockQty,
      items: wedgelocks.length
    });

    if (actualWedgelockQty < requiredWedgelockQty) {
      console.log('[Cart Kit Validation] Insufficient wedgelocks - validation failed');
      return {
        valid: false,
        message: 'Heatframe only sold with matching wedgelocks and ejectors',
        missingComponents: ['wedgelocks'],
        required: { wedgelocks: requiredWedgelockQty },
        actual: { wedgelocks: actualWedgelockQty },
        configUrl: this.getConfiguratorUrl()
      };
    }

    // Validate left ejectors
    const ejectorsLeft = cart.items.filter(item => item.sku === configuredEjectorLeftSku);
    const actualEjectorLeftQty = ejectorsLeft.reduce((sum, item) => sum + item.quantity, 0);

    console.log('[Cart Kit Validation] Left ejector validation:', {
      required: requiredEjectorLeftQty,
      actual: actualEjectorLeftQty,
      items: ejectorsLeft.length
    });

    if (actualEjectorLeftQty < requiredEjectorLeftQty) {
      console.log('[Cart Kit Validation] Insufficient left ejectors - validation failed');
      return {
        valid: false,
        message: 'Heatframe only sold with matching wedgelocks and ejectors',
        missingComponents: ['ejectors'],
        required: { ejectorLeft: requiredEjectorLeftQty },
        actual: { ejectorLeft: actualEjectorLeftQty },
        configUrl: this.getConfiguratorUrl()
      };
    }

    // Validate right ejectors (if not universal)
    if (!isUniversal) {
      const ejectorsRight = cart.items.filter(item => item.sku === configuredEjectorRightSku);
      const actualEjectorRightQty = ejectorsRight.reduce((sum, item) => sum + item.quantity, 0);

      console.log('[Cart Kit Validation] Right ejector validation:', {
        required: requiredEjectorRightQty,
        actual: actualEjectorRightQty,
        items: ejectorsRight.length
      });

      if (actualEjectorRightQty < requiredEjectorRightQty) {
        console.log('[Cart Kit Validation] Insufficient right ejectors - validation failed');
        return {
          valid: false,
          message: 'Heatframe only sold with matching wedgelocks and ejectors',
          missingComponents: ['ejectors'],
          required: { ejectorRight: requiredEjectorRightQty },
          actual: { ejectorRight: actualEjectorRightQty },
          configUrl: this.getConfiguratorUrl()
        };
      }
    } else {
      console.log('[Cart Kit Validation] Universal ejector mode - skipping right ejector validation');
    }

    // All validations passed
    console.log('[Cart Kit Validation] All validations passed - kit is complete');
    return { valid: true };
  }

  /**
   * Checks if a SKU is valid (not empty, null, undefined, or placeholder)
   * @param {string} sku - The SKU to validate
   * @returns {boolean} True if valid
   */
  isValidSku(sku) {
    return sku &&
           typeof sku === 'string' &&
           sku.trim() !== '' &&
           sku !== 'null' &&
           sku !== 'undefined' &&
           sku !== 'PLACEHOLDER';
  }

  /**
   * Gets the configurator URL for the error message link
   * @returns {string} The configurator URL
   */
  getConfiguratorUrl() {
    // Check if we're on a product page with opencots config
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('/products/')) {
      return currentUrl;
    }
    // Default to the first product with opencots config (you may need to adjust this)
    return '/products/opencots-ruggedization-kit';
  }

  /**
   * Updates the UI based on validation results
   * @param {Object} validationResult - The result from validateHeatframeKits
   */
  updateUI(validationResult) {
    this.validationResultCache = validationResult;

    // Update error banner
    this.updateErrorBanner(validationResult);

    // Update checkout button state
    this.updateCheckoutButton(validationResult);
  }

  /**
   * Updates the error banner visibility and content
   * @param {Object} validationResult - The validation result
   */
  updateErrorBanner(validationResult) {
    const errorContainer = document.getElementById('cart-kit-validation-error');

    if (!errorContainer) {
      console.warn('Cart kit validation error container not found');
      return;
    }

    if (validationResult.valid) {
      errorContainer.style.display = 'none';
      errorContainer.setAttribute('aria-hidden', 'true');
    } else {
      errorContainer.style.display = 'block';
      errorContainer.setAttribute('aria-hidden', 'false');

      // Update error message
      const messageElement = errorContainer.querySelector('.cart-kit-validation-error__message');
      if (messageElement) {
        messageElement.textContent = validationResult.message;
      }

      // Update configurator link
      const linkElement = errorContainer.querySelector('.cart-kit-validation-error__link');
      if (linkElement && validationResult.configUrl) {
        linkElement.href = validationResult.configUrl;
      }
    }
  }

  /**
   * Disables or enables the checkout button based on validation
   * @param {Object} validationResult - The validation result
   */
  updateCheckoutButton(validationResult) {
    const checkoutButton = document.getElementById('checkout');
    const dynamicCheckoutButtons = document.querySelector('.cart__dynamic-checkout-buttons');

    if (!checkoutButton) {
      console.warn('Checkout button not found');
      return;
    }

    if (validationResult.valid) {
      checkoutButton.disabled = false;
      checkoutButton.classList.remove('disabled');
      if (dynamicCheckoutButtons) {
        dynamicCheckoutButtons.style.pointerEvents = '';
        dynamicCheckoutButtons.style.opacity = '';
      }
    } else {
      checkoutButton.disabled = true;
      checkoutButton.classList.add('disabled');
      // Also disable dynamic checkout buttons (Shop Pay, PayPal, etc.)
      if (dynamicCheckoutButtons) {
        dynamicCheckoutButtons.style.pointerEvents = 'none';
        dynamicCheckoutButtons.style.opacity = '0.5';
      }
    }
  }

  /**
   * Fetches the current cart and runs validation
   */
  async validateCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const validationResult = this.validateHeatframeKits(cart);
      this.updateUI(validationResult);

      return validationResult;
    } catch (error) {
      console.error('Cart validation error:', error);
      // On error, allow checkout to proceed (fail open)
      this.updateUI({ valid: true });
      return { valid: true, error: error.message };
    }
  }

  /**
   * Initializes the validator and sets up event listeners
   */
  init() {
    // Validate on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validateCurrentCart());
    } else {
      this.validateCurrentCart();
    }

    // Listen for cart updates (using PubSub if available)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Small delay to ensure cart has been updated
        setTimeout(() => this.validateCurrentCart(), 100);
      });
    }

    // Also listen for cart changes via custom events (fallback)
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.validateCurrentCart(), 100);
    });
  }
}

// Initialize validator
const cartKitValidator = new CartKitValidator();

// Export for use in other modules if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartKitValidator;
}

// Auto-initialize when script loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => cartKitValidator.init());
} else {
  cartKitValidator.init();
}
